import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import {
  IterableMap,
  IterableMapofMap,
  genericMemo,
} from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import CheckIcon from "@redotech/redo-web/icon/check.svg";
import { Input } from "@redotech/ui/form";
import * as classNames from "classnames";
import {
  Fragment,
  Key,
  ReactElement,
  ReactNode,
  useEffect,
  useId,
  useState,
} from "react";
import { Dropdown, DropdownOption } from "./dropdown";
import ChevronDownIcon from "./icon/chevron-down.svg";
import { LabeledInput } from "./labeled-input";
import * as selectDropdownCss from "./select-dropdown.module.css";

export interface OptionConfig {
  id: string;
  name: string;
  Render?(): ReactElement;
}

export interface BaseDropdownProps<T> {
  children(option: T): ReactNode;
  keyFn?(option: T, index: number): Key;
  id?: string;
  options: readonly T[][] | readonly T[];
  className?: string;
}

export interface SingleSelectDropdownProps<T> extends BaseDropdownProps<T> {
  value: T | undefined;
  valueChange(value: T | undefined): void;
  display?(option: T): ReactNode;
  placeholder?: string;
  disabled?: boolean;
  allowNull?: boolean;
}

export interface MultiSelectDropdownProps<T> extends BaseDropdownProps<T> {
  value: readonly T[] | undefined;
  valueChange(value: readonly T[] | undefined): void;
  display?(option: readonly T[]): ReactNode;
  closeOnSelect?: boolean;
  disabled?: boolean;
  flip?: boolean; // false prevents dropdowns from changing from down to up when there's not enough space
  placement?: string;
}

export const SelectDropdown = genericMemo(function <T>({
  children,
  value,
  valueChange,
  display,
  placeholder = "",
  disabled = false,
  allowNull = false,
  ...props
}: SingleSelectDropdownProps<T>) {
  const [arrayValue, setArrayValue] = useState<[T?]>([]);

  useEffect(() => {
    if (value === undefined) {
      setArrayValue([]);
    } else {
      setArrayValue([value]);
    }
  }, [value]);

  const onChange = useHandler((newValue: T[]) => {
    if (newValue.length) {
      // The last value is the newest value
      valueChange(newValue[newValue.length - 1]);
    }
  });

  const singleDisplay = useHandler((option: T[]) => {
    if (allowNull) {
      if (option?.[0] !== undefined) {
        return display ? display(option[0]) : children(option[0]);
      }
    } else {
      if (option?.[0] !== undefined && option?.[0] !== null) {
        return display ? display(option[0]) : children(option[0]);
      }
    }
    return placeholder;
  });

  return (
    <MultiSelectDropdown
      {...props}
      value={arrayValue}
      valueChange={onChange}
      display={singleDisplay}
      closeOnSelect
      disabled={disabled}
    >
      {children}
    </MultiSelectDropdown>
  );
});

export const MultiSelectDropdown = genericMemo(function <T>({
  children,
  display,
  keyFn = (_, index) => index,
  id, // TODO: implement
  options,
  value,
  valueChange,
  className,
  closeOnSelect = false,
  disabled = false,
  flip = true,
  placement = undefined,
}: MultiSelectDropdownProps<T>) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [popover, setPopover] = useState<HTMLElement | null>(null);

  const [open, setOpen] = useState(false);

  const onClickAway = useHandler((e: MouseEvent | TouchEvent) => {
    // Don't close if toggling an option in a multi-select dropdown
    if (!closeOnSelect && popover?.contains(e.target as Node)) {
      return;
    }
    setOpen(false);
  });
  let label: ReactNode;
  if (display) {
    label = display(value || []);
  } else {
    label = value?.map((v, i) => (
      <Fragment key={keyFn(v, i)}>{children(v)}</Fragment>
    ));
  }

  const getDropdownOption = (option: T, index: number) => {
    const action = useHandler(() => {
      if (value?.includes(option)) {
        valueChange(value.filter((v) => v !== option));
      } else {
        valueChange([...(value || []), option]);
      }
    });
    return (
      <DropdownOption action={action} disabled={disabled}>
        <div>{children(option)}</div>
        {value?.includes(option) && (
          <CheckIcon className={selectDropdownCss.check} />
        )}
      </DropdownOption>
    );
  };

  return (
    <div
      className={classNames(selectDropdownCss.container, className)}
      ref={setContainer}
    >
      <ClickAwayListener onClickAway={onClickAway} touchEvent={false}>
        <div
          className={selectDropdownCss.select}
          onClick={() => setOpen((value) => !value)}
        >
          <div className={selectDropdownCss.selectValue}>{label}</div>
          <ChevronDownIcon className={selectDropdownCss.selectIcon} />
        </div>
      </ClickAwayListener>
      <Dropdown
        anchor={container}
        open={open}
        ref={setPopover}
        placement={placement}
        flip={flip}
      >
        {Array.isArray(options[0]) ? (
          <IterableMapofMap
            keyFn={keyFn}
            items={options as T[][]}
            separator={<hr className={selectDropdownCss.line} />}
          >
            {getDropdownOption}
          </IterableMapofMap>
        ) : (
          <IterableMap keyFn={keyFn} items={options as T[]}>
            {getDropdownOption}
          </IterableMap>
        )}
      </Dropdown>
    </div>
  );
});

export const FormSelectDropdown = genericMemo(function <T>({
  description,
  label,
  input,
  valueChange,
  disabled = false,
  ...props
}: {
  description?: ReactNode | ReactNode[];
  label: string;
  input: Input<T>;
  valueChange?(value: T): void;
  disabled?: boolean;
} & Omit<SingleSelectDropdownProps<T>, "value" | "valueChange">) {
  const id = useId();
  const onChange = (value: T) => {
    input.setValue(value);
    if (valueChange) {
      valueChange(value);
    }
  };
  return (
    <LabeledInput
      errors={input.changed ? input.errors : []}
      label={label}
      description={description}
      id={id}
    >
      <SelectDropdown
        value={input.value}
        valueChange={onChange}
        disabled={disabled}
        {...props}
      />
    </LabeledInput>
  );
});

export const FormMultiSelectDropdown = genericMemo(function <T>({
  description,
  label,
  input,
  valueChange,
  ...props
}: {
  description?: ReactNode | ReactNode[];
  label: string;
  valueChange?(value: T[]): void;
  input: Input<T[]>;
} & Omit<MultiSelectDropdownProps<T>, "value" | "valueChange">) {
  const id = useId();
  const onChange = (value: T[]) => {
    input.setValue(value);
    if (valueChange) {
      valueChange(value);
    }
  };
  return (
    <LabeledInput
      errors={input.changed ? input.errors : []}
      label={label}
      description={description}
      id={id}
    >
      <MultiSelectDropdown
        value={input.value}
        valueChange={onChange}
        {...props}
      />
    </LabeledInput>
  );
});
